import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Leaf, ArrowLeft, Mail, KeyRound, Lock, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: 输入邮箱, 2: 输入验证码, 3: 设置新密码, 4: 完成
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState(''); // 开发模式显示验证码
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 步骤1: 发送验证码
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('forgotPassword.enterEmail', '请输入邮箱地址'));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success(t('forgotPassword.codeSent', '验证码已发送'));
      // 开发模式下显示验证码
      if (res.data.dev_code) {
        setDevCode(res.data.dev_code);
      }
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // 步骤2: 验证验证码
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast.error(t('forgotPassword.enterCode', '请输入6位验证码'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-reset-code`, { email, code });
      toast.success(t('forgotPassword.codeVerified', '验证码正确'));
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('forgotPassword.invalidCode', '验证码无效或已过期'));
    } finally {
      setLoading(false);
    }
  };

  // 步骤3: 重置密码
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error(t('forgotPassword.passwordTooShort', '密码至少需要6位'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('forgotPassword.passwordMismatch', '两次输入的密码不一致'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        email, 
        code, 
        new_password: newPassword 
      });
      toast.success(t('forgotPassword.resetSuccess', '密码重置成功'));
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-500 rounded-full flex items-center justify-center">
              {step === 4 ? (
                <CheckCircle className="w-8 h-8 text-white" />
              ) : step === 3 ? (
                <Lock className="w-8 h-8 text-white" />
              ) : step === 2 ? (
                <KeyRound className="w-8 h-8 text-white" />
              ) : (
                <Mail className="w-8 h-8 text-white" />
              )}
            </div>
          </div>
          <CardTitle className="text-2xl">
            {step === 4 
              ? t('forgotPassword.resetComplete', '重置完成') 
              : step === 3 
                ? t('forgotPassword.setNewPassword', '设置新密码')
                : step === 2 
                  ? t('forgotPassword.enterVerificationCode', '输入验证码')
                  : t('forgotPassword.title', '找回密码')}
          </CardTitle>
          <CardDescription>
            {step === 4 
              ? t('forgotPassword.canNowLogin', '您可以使用新密码登录了')
              : step === 3 
                ? t('forgotPassword.setNewPasswordDesc', '请设置您的新密码')
                : step === 2 
                  ? t('forgotPassword.checkEmail', '验证码已发送到您的邮箱')
                  : t('forgotPassword.enterEmailDesc', '输入注册邮箱，我们将发送验证码')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* 步骤指示器 */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
                  ${step >= s 
                    ? 'bg-gradient-to-br from-purple-600 to-pink-500 text-white' 
                    : 'bg-gray-200 text-amber-600'}`}>
                  {step > s ? '✓' : s}
                </div>
                {s < 3 && <div className={`w-8 h-1 ${step > s ? 'bg-purple-500' : 'bg-gray-200'}`}></div>}
              </div>
            ))}
          </div>

          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600" 
                disabled={loading}
                data-testid="send-code-btn"
              >
                {loading ? t('common.loading', '加载中...') : t('forgotPassword.sendCode', '发送验证码')}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              {/* 开发模式提示 */}
              {devCode && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-xs text-yellow-700 mb-1">{t('forgotPassword.devMode', '开发模式 - 验证码')}:</p>
                  <p className="text-2xl font-bold text-yellow-800 tracking-widest">{devCode}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="code">{t('forgotPassword.verificationCode', '验证码')}</Label>
                <Input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                  required
                  data-testid="verification-code-input"
                />
                <p className="text-xs text-amber-600 text-center">
                  {t('forgotPassword.codeExpiry', '验证码10分钟内有效')}
                </p>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600" 
                disabled={loading || code.length !== 6}
                data-testid="verify-code-btn"
              >
                {loading ? t('common.loading', '加载中...') : t('forgotPassword.verify', '验证')}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full"
                onClick={() => { setStep(1); setCode(''); setDevCode(''); }}
              >
                {t('forgotPassword.resend', '重新发送')}
              </Button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('forgotPassword.newPassword', '新密码')}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••"
                  minLength={6}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('forgotPassword.confirmPassword', '确认密码')}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••"
                  minLength={6}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600" 
                disabled={loading}
                data-testid="reset-password-btn"
              >
                {loading ? t('common.loading', '加载中...') : t('forgotPassword.resetPassword', '重置密码')}
              </Button>
            </form>
          )}

          {step === 4 && (
            <div className="text-center space-y-4">
              <div className="py-4">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <p className="text-amber-700">{t('forgotPassword.successMessage', '您的密码已成功重置')}</p>
              </div>
              <Button 
                onClick={() => navigate('/login')} 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600"
                data-testid="go-login-btn"
              >
                {t('forgotPassword.goToLogin', '前往登录')}
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter className="justify-center">
          <Button variant="link" onClick={() => navigate('/login')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('forgotPassword.backToLogin', '返回登录')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
